/**
 * Reads the duration of an OGG/Opus file straight from its container,
 * instead of relying on the browser's `<audio>` element to figure it out.
 *
 * Voice notes mirrored from WhatsApp arrive without a duration index, so
 * `<audio>.duration` resolves to `Infinity`/`NaN` until the engine is forced
 * to scan the file (see the seek-to-end trick in chips/Audio.vue) — a trick
 * that doesn't always complete. Parsing the OGG pages ourselves is
 * deterministic: Opus's granule position is always clocked at 48 kHz
 * regardless of the input sample rate declared in OpusHead, so the last
 * page's granule position divided by 48000 is the exact duration.
 *
 * @param {ArrayBuffer} buffer
 * @returns {number|null} duration in seconds, or null if this isn't a
 *   parseable OGG bitstream.
 */
export function getOggOpusDuration(buffer) {
  const data = new Uint8Array(buffer);
  let pos = 0;
  let lastGranule = null;

  while (pos + 27 <= data.length) {
    if (
      data[pos] !== 0x4f ||
      data[pos + 1] !== 0x67 ||
      data[pos + 2] !== 0x67 ||
      data[pos + 3] !== 0x53
    ) {
      break;
    }

    const nseg = data[pos + 26];
    if (pos + 27 + nseg > data.length) break;

    const dv = new DataView(data.buffer, data.byteOffset + pos, 27);
    const granuleLo = dv.getUint32(6, true);
    const granuleHi = dv.getUint32(10, true);
    lastGranule = granuleHi * 0x100000000 + granuleLo;

    let segLen = 0;
    for (let i = 0; i < nseg; i += 1) segLen += data[pos + 27 + i];
    pos += 27 + nseg + segLen;
  }

  if (lastGranule === null) return null;
  return lastGranule / 48000;
}
